import React, { useEffect, useMemo, useState } from 'react';
import { format, parseISO } from "date-fns";
import "react-datepicker/dist/react-datepicker.css";
import fetchMetricData from '../../../dataManagement/fetchMetricData.js';
import Slider from 'rc-slider';
import 'rc-slider/assets/index.css';

const GlobalTimeseriesSlider = ({ selectedYearEditing, globalState, globalRefs, setSelectedYearEditing, ResetMap }) => {

    const longstandingGeoJSONRef = globalRefs.longstandingGeoJSONRef;
    let selectedRange = globalState.selectedRange;
    let setSelectedRange = globalState.setSelectedRange;
    let selectedAggregate = globalState.selectedAggregate;
    let setSelectedAggregate = globalState.setSelectedAggregate;
    let selectedKey = globalState.selectedKey;
    let setLandKeySelectedKey = globalState.setLandKeySelectedKey;
    let selectedMetric = globalState.selectedMetric;
    let polygonCustomLayerRef = globalRefs.polygonCustomLayerRef;
    let multuGroupSelectedArray = globalState.multuGroupSelectedArray;
    let featureList = globalState.featureList;
    let activeFeatureId = globalState.activeFeatureId;
    let mapRef = globalRefs.mapRef;
    let mapMetaData = globalState.mapMetaData;
    let isYearRangeActive = globalState.isYearRangeOpen;
    let setIsYearRangeActive = globalState.setIsYearRangeOpen;
    let setSelectedMetric = globalState.setSelectedMetric;
    let availableValues = globalState.availableValues;
    let setSelectedKey = globalState.setSelectedKey;
    let availableKeys = globalState.availableKeys;
    let numericSummary = globalState.numericSummary;
    let setNumericSummary = globalState.setNumericSummary;
    let keySummary = globalState.keySummary;
    let setKeySummary = globalState.setKeySummary;
    let globalStartDate = globalState.globalStartDate;
    let setGlobalStartDate = globalState.setGlobalStartDate;
    let globalEndDate = globalState.globalEndDate;
    let setGlobalEndDate = globalState.setGlobalEndDate;
    let availableNumericMetrics = globalState.availableNumericMetrics;
    let setAvailableNumericMetrics = globalState.setAvailableNumericMetrics;
    let availableStringMetrics = globalState.availableStringMetrics;
    let setAvailableStringMetrics = globalState.setAvailableStringMetrics;
    let debugMode = globalState.debugMode;
    let setDebugMode = globalState.setDebugMode;
    let availableDates = globalState.availableDates;
    let setAvailableDates = globalState.setAvailableDates;
    let availableIDs = globalState.availableIDs;
    let setAvailableIDs = globalState.setAvailableIDs;

    const [scrubIndex, setScrubIndex] = useState(0);

    const MAX_SLIDER_OPTIONS = 40;
    const tooManyOptions = availableDates.length > MAX_SLIDER_OPTIONS;

    // NEW SLIDER STUFF BELOW

    const marks = useMemo(() => {
        return availableDates.reduce((acc, y, i) => {
            const date = new Date(y);

            const day = String(date.getDate()).padStart(2, "0");
            const month = String(date.getMonth() + 1).padStart(2, "0");
            const year = String(date.getFullYear()).slice(-2);

            acc[i] = `${day}/${month}/${year}`;

            return acc;
        }, {});
    }, [availableDates]);

    const toIsoWithOffset = (date) => {
        const d = new Date(date);
        const pad = (n) => String(n).padStart(2, '0');
        const year = d.getUTCFullYear();
        const month = pad(d.getUTCMonth() + 1);
        const day = pad(d.getUTCDate());
        const hours = pad(d.getUTCHours());
        const minutes = pad(d.getUTCMinutes());
        const seconds = pad(d.getUTCSeconds());
        return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}+00:00`;
    };

    // ------------------ API Call on Aggregate Change ------------------

    const [geoJSONReadyVersion, setGeoJSONReadyVersion] = useState(0);

    useEffect(() => {

        if (debugMode) {
            const timestamp = new Date().toISOString();
            console.log(timestamp + " " + "Map Meta Check:");
        }
        if (debugMode && mapMetaData === null) {
            const timestamp = new Date().toISOString();
            console.log(timestamp + " " + "❌ Map Meta empty");
            return
        } else {
            const timestamp = new Date().toISOString();
            console.log(timestamp + " " + "✅ Map Meta full, continuing");
        }

        const convertCoordinates = (geometry) => {
            if (geometry.type === "Point") return geometry.coordinates;
            if (geometry.type === "Polygon") {
                return [geometry.coordinates[0].map((coord) => [coord[0], coord[1]])];
            }
            if (geometry.type === "MultiPolygon") {
                return geometry.coordinates.map((poly) => [
                    poly[0].map((coord) => [coord[0], coord[1]]),
                ]);
            }
            throw new Error(`Unsupported geometry type: ${geometry.type}`);
        };

        const fetchData = async () => {
            if (debugMode) {
                const timestamp = new Date().toISOString();
                console.log(timestamp + " " + "🚀 .01 Fetching Full Timeseries Because new map loaded")
            }
            const fullTimeseriesForSelectionResult = await fetchMetricData({
                id: globalRefs.currentMapIdRef.current,
                workingLocally: globalState.workingLocally,
                devDeployment: globalState.devDeployment,
            }); const timestamp = new Date().toISOString();
            console.log(timestamp + " " + "🩻 HEALTH CHECK OF RETURNED INITIAL DATA FROM API:")
            if (fullTimeseriesForSelectionResult === null) {
                const timestamp = new Date().toISOString();
                console.log(timestamp + " " + "☠️ 🩻 HEALTH CHECK FAILED")
                return false;
            } else {
                const timestamp = new Date().toISOString();
                console.log(timestamp + " " + "✅ 🩻 HEALTH CHECK SUCCESSFUL")
            }
            if (Array.isArray(fullTimeseriesForSelectionResult)) {
                const features = fullTimeseriesForSelectionResult.map((feature) => ({
                    ...feature,
                    geometry: {
                        ...feature.geometry,
                        coordinates: convertCoordinates(feature.geometry),
                    },
                }));
                longstandingGeoJSONRef.current = {
                    type: "FeatureCollection",
                    features,
                };
                setGeoJSONReadyVersion(v => v + 1);
                if (debugMode) {
                    const timestamp = new Date().toISOString();
                    console.log(timestamp + " " + "🚀 .02 New Full Timeseries")
                }
            }
        };
        const timestamp = new Date().toISOString();
        console.log(timestamp + " " + "✅ Initial Map Meta grab results: " + mapMetaData)
        fetchData();
    }, [mapMetaData]);

    // ------------------ Handlers ------------------

    const handleSelectDate = (date) => {
        console.log(date)
        setSelectedYearEditing(date);
    };

    const [clickerToReseteatureMarkers, setclickerToReseteatureMarkers] = useState(0);

    // ------------------ Render ------------------

    const [activeField, setActiveField] = useState(null); // "start" | "end"

    // ------------------ Extract Dates from the first feature and apply it to the inital date range ------------------

    const extractDatesFromAllSeries = (geojson) => {
        const features = geojson?.features;
        if (!Array.isArray(features) || features.length === 0) return [];

        const allKeys = new Set();

        for (const feature of features) {
            const ts = feature?.feature_data_simple_timeseries;
            if (!ts || typeof ts !== "object") continue;

            for (const series of Object.values(ts)) {
                if (!series || typeof series !== "object") continue;

                Object.keys(series).forEach(key => allKeys.add(key));
            }
        }

        return [...allKeys]
            .map(d => parseISO(d))
            .filter(Boolean)
            .sort((a, b) => a - b);
    };

    useEffect(() => {
        if (!mapMetaData?.define_feature_data_simple_timeseries) return;
        const metaMetrics = mapMetaData.define_feature_data_simple_timeseries;
        const numericMetrics = [];
        const stringMetrics = [];

        Object.keys(metaMetrics).forEach(metric => {
            const meta = metaMetrics[metric];

            const hasData = longstandingGeoJSONRef.current?.features?.some(feature => {
                const series = feature.feature_data_simple_timeseries?.[metric];
                if (!series) return false;

                return Object.values(series).some(value => {
                    return (
                        value !== null &&
                        value !== undefined &&
                        value !== "" &&
                        value !== "No data available"
                    );
                });
            });

            if (!hasData) return;

            if (meta?.type === "number") {
                numericMetrics.push(metric);
            } else {
                stringMetrics.push(metric);
            }
        });

        setAvailableNumericMetrics(numericMetrics);
        setAvailableStringMetrics(stringMetrics);

    }, [
        mapMetaData,
        longstandingGeoJSONRef.current,
        selectedYearEditing
    ]);

    const applyDateRange = (nextStart, nextEnd) => {
        if (debugMode) {
            const timestamp = new Date().toISOString();
            console.log(timestamp + " " + "🚀 [1] applyDateRange START");
        }
        if (!nextStart || !nextEnd) {
            if (debugMode) {

                const timestamp = new Date().toISOString();
                console.log(timestamp + " " + "❌ Missing start or end date");
            }
            return;
        }
        if (debugMode) {
            const timestamp = new Date().toISOString();
            console.log(timestamp + " " + "📅 [2] Incoming Range", {
                nextStart,
                nextEnd,
            });
        }
        const allDates = extractDatesFromAllSeries(
            longstandingGeoJSONRef.current
        );
        if (debugMode) {
            const timestamp = new Date().toISOString();
            console.log(timestamp + " " + "📦 [3] Extracted All Dates", {
                total: allDates.length,
                sample: allDates.slice(0, 3),
            });
        }
        const filteredDates = allDates.filter(
            d => d >= nextStart && d <= nextEnd
        );
        if (debugMode) {
            const timestamp = new Date().toISOString();
            console.log(timestamp + " " + "🔍 [4] Filtered Dates", {
                total: filteredDates.length,
                sample: filteredDates.slice(0, 3),
            });
        }
        const formattedDates = filteredDates.map(d =>
            format(d, "yyyy-MM-dd'T'HH:mm:ssxxx")
        );
        if (debugMode) {
            const timestamp = new Date().toISOString();
            console.log(timestamp + " " + "🧹 [5] Formatted Dates", {
                total: formattedDates.length,
                first: formattedDates[0],
                last: formattedDates[formattedDates.length - 1],
            });
        }
        const nextScrubIndex = 0;
        if (debugMode) {
            const timestamp = new Date().toISOString();
            console.log(timestamp + " " + "🎚️ [6] Next Scrub Index", nextScrubIndex);
        }
        const nextSelectedYearEditing = formattedDates[0] ?? null;
        if (debugMode) {
            console.log(
                "📆 [7] Next Selected Year Editing",
                nextSelectedYearEditing
            );
        }

        const timeseriesMeta =
            mapMetaData?.define_feature_data_simple_timeseries ?? {};

        const getDisplayOrderApprovedItem = (items) => {
            return (
                [...(items ?? [])]
                    .filter(item => {
                        const order = timeseriesMeta[item]?.display_order;
                        return order !== -1;
                    })
                    .sort((a, b) => {
                        const orderA =
                            timeseriesMeta[a]?.display_order ?? 999999;

                        const orderB =
                            timeseriesMeta[b]?.display_order ?? 999999;

                        return orderA - orderB;
                    })
                [0] ?? null
            );
        };

        const nextSelectedValue = getDisplayOrderApprovedItem(availableValues);

        const nextSelectedKey = getDisplayOrderApprovedItem(availableKeys);

        if (debugMode) {
            const timestamp = new Date().toISOString();
            console.log(timestamp + " " + "🗝️ [9] Next Selected Key", nextSelectedKey);
            console.log(timestamp + " " + "⚡ [10] Applying React State Updates...");
        }
        setGlobalStartDate(nextStart);
        if (debugMode) {
            const timestamp = new Date().toISOString();
            console.log(timestamp + " " + "✅ setGlobalStartDate");
        }
        setGlobalEndDate(nextEnd);
        if (debugMode) {
            const timestamp = new Date().toISOString();
            console.log(timestamp + " " + "✅ setGlobalEndDate");
        }
        setSelectedRange([nextStart, nextEnd]);
        if (debugMode) {
            const timestamp = new Date().toISOString();
            console.log(timestamp + " " + "✅ setSelectedRange");
        }
        setAvailableDates(formattedDates);
        if (debugMode) {
            const timestamp = new Date().toISOString();
            console.log(timestamp + " " + "✅ setAvailableDates");
        }
        setScrubIndex(nextScrubIndex);
        if (debugMode) {
            const timestamp = new Date().toISOString();
            console.log(timestamp + " " + "✅ setScrubIndex");
        }
        setSelectedYearEditing(nextSelectedYearEditing);
        if (debugMode) {
            const timestamp = new Date().toISOString();
            console.log(timestamp + " " + "✅ setSelectedYearEditing");
        }
        if (nextSelectedValue !== null) {
            setSelectedMetric(nextSelectedValue);
        }
        if (nextSelectedKey !== null) {
            setSelectedKey(nextSelectedKey);
            setLandKeySelectedKey(nextSelectedKey);
        }
        setLandKeySelectedKey(nextSelectedKey);
        if (debugMode) {
            const timestamp = new Date().toISOString();
            console.log(timestamp + " " + "✅ setLandKeySelectedKey");
            console.log(timestamp + " " + "🏁 [11] applyDateRange COMPLETE");
        }
    };

    useEffect(() => {
        if (
            !globalStartDate ||
            !globalEndDate ||
            !selectedYearEditing
        ) {
            return;
        }
        if (debugMode) {
            const timestamp = new Date().toISOString();
            console.log(timestamp + " " + "🏁 [12] resetting Map")
        }
        ResetMap(globalRefs, globalState, () => {
            setTimeout(
                () => setclickerToReseteatureMarkers(t => t + 1),
                100
            );
        });
    }, [
        globalStartDate,
        globalEndDate,
        scrubIndex,
        selectedYearEditing,
        selectedMetric,
        selectedKey
    ]);

    useEffect(() => {
        const geojson = longstandingGeoJSONRef.current;
        if (!geojson) return;
        const hasAvailableValues = Array.isArray(availableValues) && availableValues.length > 0;
        const hasAvailableKeys = Array.isArray(availableKeys) && availableKeys.length > 0;
        const allDates = extractDatesFromAllSeries(geojson);
        if (!allDates.length) return;
        applyDateRange(allDates[0], allDates[allDates.length - 1]);
    }, [geoJSONReadyVersion, availableValues, availableKeys]);

    const handleDateChange = (date) => {
        if (activeField === "start") {
            const nextStart = date;
            const nextEnd = globalEndDate && globalEndDate >= nextStart ? globalEndDate : nextStart;
            applyDateRange(nextStart, nextEnd);
        }
        if (activeField === "end") {
            const nextEnd = date;
            const nextStart = globalStartDate && globalStartDate <= nextEnd ? globalStartDate : nextEnd;
            applyDateRange(nextStart, nextEnd);
        }
        setActiveField(null);
    };

    // ESC key closes picker
    useEffect(() => {
        const handleEsc = (e) => {
            if (e.key === "Escape") {
                setActiveField(null);
            }
        };
        window.addEventListener("keydown", handleEsc);
        return () => window.removeEventListener("keydown", handleEsc);
    }, [setActiveField]);

    useEffect(() => {
        if (activeField) {
            setIsYearRangeActive(true);
        } else {
            setIsYearRangeActive(false);
        }
    }, [activeField, setIsYearRangeActive]);

    return (
        <div style={{
            bottom: '0px',
            width: '600px',
            height: '70px',
            left: '50%',
            transform: 'translate(-50%)',
            position: 'absolute',
            // backgroundColor: '#ff0000',
            zIndex: 10000,
            borderBottom: '1px solid #ccc',
        }}>

            {/* SLIDER */}

            <div className="modalBackdrop" style={{
                backgroundColor: "transparent",
                // padding: '0px 0px 0px 30px',
                marginTop: '0px',
                width: '100%',
                height: '60px'
                // display: 'none'
            }}>
                <Slider
                    className="timestamp-slider"
                    style={{
                        top: "0px",
                        // width: "310px",
                        left: "5px"
                    }}
                    min={0}
                    max={Math.max(0, availableDates.length - 1)}
                    value={Math.max(
                        0,
                        availableDates.findIndex(
                            y =>
                                new Date(y).getTime() ===
                                new Date(selectedYearEditing).getTime()
                        )
                    )}
                    marks={
                        Object.fromEntries(
                            Object.entries(marks).map(([key, value]) => [
                                key,
                                {
                                    label: (
                                        <div
                                            style={{
                                                transform: "rotate(-45deg)",
                                                whiteSpace: "nowrap",
                                                fontSize: "8px",
                                                marginTop: "10px"
                                            }}
                                        >
                                            {value}
                                        </div>
                                    )
                                }
                            ])
                        )
                    }
                    step={1}
                    onChange={(val) => {
                        const year = availableDates[val];
                        setSelectedYearEditing(
                            toIsoWithOffset(year)
                        );
                    }}
                />
            </div>
        </div>
    );
};

export default GlobalTimeseriesSlider;